import React, { useContext, useMemo } from "react";
import { ReactSVG } from "react-svg";

import arrowIcon from "../../../assets/svg/arrow-s.svg";
import RatingStars from "../../Common/RatingStars";
import { getRatingAccentColor } from "../../Common/ratingPalette";
import { StudentInfoContext } from "./StudentInfoContext";

function formatRatingNumber(value) {
   const number = Number(value);
   if (!Number.isFinite(number)) return "—";
   return number % 1 === 0 ? String(number) : number.toFixed(1);
}

function getInstructorName(rating) {
   const person = rating?.instructor;
   if (!person) return "Instructor necunoscut";
   const first = String(person.firstName || "").trim();
   const last = String(person.lastName || "").trim();
   const full = `${first} ${last}`.trim();
   return full || person.email || "Instructor necunoscut";
}

function getReviewDateLabel(rating) {
   const raw =
      rating?.reservation?.startTime ||
      rating?.reservation?.date ||
      rating?.createdAt ||
      rating?.updatedAt;
   if (!raw) return "";

   const date = new Date(raw);
   if (Number.isNaN(date.getTime())) return "";

   return date.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
   });
}

function getReviewSortMs(rating) {
   const raw =
      rating?.createdAt ||
      rating?.updatedAt ||
      rating?.reservation?.startTime ||
      0;
   const ms = new Date(raw).getTime();
   return Number.isFinite(ms) ? ms : 0;
}

function getReviewId(rating) {
   return (
      rating?.id ??
      `${rating?.reservationId || "r"}-${rating?.instructorId || "i"}-${
         rating?.createdAt || "c"
      }`
   );
}

export default function StudentReviewsTab() {
   const {
      reviews,
      reviewsLoading,
      reviewsError,
      reviewsAscending,
      setReviewsAscending,
   } = useContext(StudentInfoContext);

   const sortedReviews = useMemo(() => {
      const clone = Array.isArray(reviews) ? [...reviews] : [];
      clone.sort((a, b) => {
         const diff = getReviewSortMs(a) - getReviewSortMs(b);
         return reviewsAscending ? diff : -diff;
      });
      return clone;
   }, [reviews, reviewsAscending]);

   return (
      <div className="studentsProfileUI__reviews">
         <div className="studentsProfileUI__reservationsMeta">
            <span className="studentsProfileUI__reservationsCount">
               {sortedReviews.length}{" "}
               {sortedReviews.length === 1 ? "recenzie" : "recenzii"}
            </span>

            <button
               type="button"
               className="studentsProfileUI__sortBtn"
               onClick={() => setReviewsAscending((prev) => !prev)}
               title={
                  reviewsAscending
                     ? "Sortează de la cele mai noi"
                     : "Sortează de la cele mai vechi"
               }
               aria-label={
                  reviewsAscending
                     ? "Sortează de la cele mai noi"
                     : "Sortează de la cele mai vechi"
               }
            >
               <ReactSVG
                  src={arrowIcon}
                  className={
                     "studentsProfileUI__sortIcon" +
                     (reviewsAscending ? "" : " is-desc")
                  }
               />
            </button>
         </div>

         {reviewsLoading ? (
            <p className="students-info__empty">Se încarcă recenziile…</p>
         ) : reviewsError ? (
            <p className="students-info__error">{reviewsError}</p>
         ) : !sortedReviews.length ? (
            <p className="students-info__empty">
               Acest elev nu a lăsat nicio recenzie.
            </p>
         ) : (
            <div className="studentsProfileUI__reviewsList">
               {sortedReviews.map((rating) => {
                  const comment = String(rating?.comment || "").trim();
                  const accent =
                     getRatingAccentColor(rating?.rating) || undefined;

                  return (
                     <article
                        key={getReviewId(rating)}
                        className="studentsProfileUI__review"
                     >
                        <div className="studentsProfileUI__reviewHead">
                           <span className="studentsProfileUI__reviewInstructor">
                              {getInstructorName(rating)}
                           </span>
                           <span className="studentsProfileUI__reviewDate">
                              {getReviewDateLabel(rating)}
                           </span>
                        </div>

                        <div className="studentsProfileUI__reviewScore">
                           <RatingStars
                              value={rating?.rating}
                              size="sm"
                              accentColor={accent}
                           />
                           <span className="studentsProfileUI__reviewValue">
                              {formatRatingNumber(rating?.rating)}
                           </span>
                        </div>

                        <p
                           className={
                              "studentsProfileUI__reviewComment" +
                              (comment ? "" : " is-empty")
                           }
                        >
                           {comment || "Fără comentariu."}
                        </p>
                     </article>
                  );
               })}
            </div>
         )}
      </div>
   );
}
